import { zodResolver } from '@hookform/resolvers/zod';
import { O } from '@mobily/ts-belt';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useErrorMessage } from 'app/ui/validationError/useErrorMessage';
import { useTranslation } from 'common/hooks/useTranslation';
import { Lambda } from 'common/types';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAreaPhotoUpload } from 'app/areas/hooks/useAreaPhotoUpload';
import { useCreateFireBreach } from '../../hooks/useCreate';
import { fireBreachSchema, FireBreachSchema } from '../../types/FireBreachSchema';
import { PhotosField } from '../PhotosField/PhotosField';

export const CreateFireBreach: FC<{ onSave: Lambda<void, void> }> = ({ onSave }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FireBreachSchema>({ mode: 'onChange', resolver: zodResolver(fireBreachSchema) });
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [formValues, setFormValues] = useState({} as O.Option<FireBreachSchema>);
  const [allChecked, setAllChecked] = useState(false);
  const [checkboxes, setCheckboxes] = useState({
    checkbox1: false,
    checkbox2: false,
    checkbox3: false,
  });
  const [photosURLs, setPhotosURLs] = useState<ReadonlyArray<string>>([]);

  // Get pre-generated entity ID and property ID for offline photo tracking
  const { entityId: preGeneratedId, entityType, propertyId } = useAreaPhotoUpload('fireBreach');

  const { mutate, isLoading } = useCreateFireBreach({
    onSuccess: () => {
      onSave();
      toast.success(t('general.createdSuccessfully'));
    },
    onError: () => {
      toast.error(t('error.generalMessage'));
    },
  });

  const getErrorMessage = useErrorMessage(errors);

  useEffect(() => {
    setAllChecked(checkboxes.checkbox1 && checkboxes.checkbox2 && checkboxes.checkbox3);
  }, [checkboxes]);

  const handleSave = (values: FireBreachSchema) => {
    setFormValues(values);
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
  };

  const handleDialogConfirm = () => {
    // Filter blob URLs and use pre-generated ID so photos are associated with this entity
    mutate({ ...formValues, id: preGeneratedId, photosURLs });
    setShowDialog(false);
  };

  const handleCheckboxChange = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxes({ ...checkboxes, [name]: event.target.checked });
  };

  return (
    <>
      <Dialog open={showDialog} onClose={handleDialogClose} PaperProps={{ sx: { margin: 0.5, p: 2 } }}>
        <DialogTitle>{t('checklist.title')}</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset">
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={checkboxes.checkbox1} onChange={handleCheckboxChange('checkbox1')} />}
                label={t('checklist.device.1')}
              />
              <FormControlLabel
                control={<Checkbox checked={checkboxes.checkbox2} onChange={handleCheckboxChange('checkbox2')} />}
                label={t('checklist.device.2')}
              />
              <FormControlLabel
                control={<Checkbox checked={checkboxes.checkbox3} onChange={handleCheckboxChange('checkbox3')} />}
                label={t('checklist.cleaned')}
              />
            </FormGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            {t('general.cancel')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleDialogConfirm} disabled={!allChecked}>
            {t('general.create')}
          </Button>
        </DialogActions>
      </Dialog>
      <Stack component="form" gap={0.5} onSubmit={handleSubmit(handleSave)}>
        <Typography sx={{ fontSize: 18, paddingBottom: 2 }}>{t('areas.fireBreach.label')}</Typography>
        <Stack gap={2} width="100%">
          <PhotosField
            value={photosURLs}
            onChange={setPhotosURLs}
            entityType={entityType}
            entityId={preGeneratedId}
            propertyId={propertyId}
          />
          <TextField label={t('areas.fields.name')} {...register('text')} helperText={getErrorMessage('text')} />
          <TextField label={t('areas.fields.location')} {...register('location')} />
        </Stack>
        <Grid container justifyContent="flex-end" mt={2}>
          <Button variant="contained" type="submit" disabled={!isValid || isLoading}>
            {isLoading ? <CircularProgress size={16} /> : t('general.save')}
          </Button>
        </Grid>
      </Stack>
    </>
  );
};
